import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import React, { Suspense, lazy } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';

import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PlusCircle } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { authClient } from '@/lib/auth-client';
import { columns } from './columns';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

const CreateApiKeyDialog = lazy(() => import('@/components/settings/api-keys/create-api-key-dialog'));

export default function ApiKeysPage() {
  const { t } = useTranslation();
  const [showCreateDialog, setShowCreateDialog] = React.useState(false);

  const {
    data: apiKeys,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['apiKeys'],
    queryFn: async () => {
      const { data, error } = await authClient.apiKey.list();
      if (error) {
        throw new Error(error.message || t('settings.apiKeys.errors.fetchFailed', 'Failed to fetch API keys'));
      }
      return data;
    },
  });

  const table = useReactTable({
    data: apiKeys ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleCreateClick = () => {
    setShowCreateDialog(true);
  };

  const handleDialogClose = () => {
    setShowCreateDialog(false);
  };

 return (
     <div className="space-y-6 p-4 md:p-6">
       <div className="flex items-center justify-between">
         <h1 className="text-2xl font-semibold">
          {t('settings.apiKeys.title', 'API Keys')}
        </h1>
        <Button onClick={handleCreateClick}>
          <PlusCircle className="mr-2 h-4 w-4" />
          {t('settings.apiKeys.createButton', 'Create API Key')}
        </Button>
      </div>

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}
      {error && (
         <Alert variant="destructive">
           <AlertTriangle className="h-4 w-4" />
           <AlertTitle>{t('common.error', 'Error')}</AlertTitle>
           <AlertDescription>{error.message}</AlertDescription>
         </Alert>
      )}
      {!isLoading && !error && (
        <div className="overflow-x-auto rounded-md">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : (
                        <div>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </div>
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    {t('sidebar.search.noResults', 'No results')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
      {showCreateDialog && (
        <Suspense fallback={null}>
          <CreateApiKeyDialog
            open={showCreateDialog}
            onOpenChange={setShowCreateDialog}
          />
        </Suspense>
      )}
     </div>
 );
}